
import { useNavigate } from 'react-router'
import { useEffect, useState, useRef } from 'react'
import CompareIcon from '../assets/images/CompareIcon'
import ThinBagIcon from '../assets/images/ThinBagIcon'
import ThinLoveIcon from '../assets/images/ThinLoveIcon'
import './topbar.css'
import { BRIGIZ_APP_E_COM_CART, BRIGIZ_APP_E_COM_COMPARE_PRODUCT, BRIGIZ_APP_E_COM_WISHLIST } from '../Utils/constants'
import { getItemLocalStorage } from '../Utils/localStroge'

const TopBar = () => {
    const navigate = useNavigate()
    const [currentLocation, setCurrentLocation] = useState("Fetching location...")
    const [isLoading, setIsLoading] = useState(true)
    const [locationError, setLocationError] = useState(false)
    
    // Language state
    const [selectedLanguage, setSelectedLanguage] = useState(null)
    const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
    const languageDropdownRef = useRef(null)

    // Default languages - you can customize this list
    const languages = [
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
        { code: 'gu', name: 'ગુજરાતી', flag: '🇮🇳' },
        { code: 'bn', name: 'বাংলা', flag: '🇧🇩' },
        { code: 'ta', name: 'தமிழ்', flag: '🇮🇳' },
        { code: 'te', name: 'తెలుగు', flag: '🇮🇳' },
        { code: 'mr', name: 'मराठी', flag: '🇮🇳' },
        { code: 'kn', name: 'ಕನ್ನಡ', flag: '🇮🇳' },
        { code: 'ml', name: 'മലയാളം', flag: '🇮🇳' },
        { code: 'pa', name: 'ਪੰਜਾਬੀ', flag: '🇮🇳' }
    ]

    // Helper function to get access token from localStorage
    const getAccessTokenFromCache = () => {
        if (typeof window !== 'undefined') {
            const accessToken = getItemLocalStorage("accessToken")
            return accessToken || ''
        }
        return ''
    }

    // Function to get user's saved addresses
    const getUserAddresses = async () => {
        const accessToken = getAccessTokenFromCache()
        if (!accessToken) return null
        
        try {
            // You'll need to replace this with your actual API endpoint
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL || ''}/api/user/address?token=${accessToken}`
            )
            
            if (response.ok) {
                const data = await response.json()
                console.log('TopBar - Address response:', data)
                
                if (data && data.addresses && data.addresses.length > 0) {
                    // Find default address (default_billing === 1)
                    const defaultAddress = data.addresses.find(addr => addr.default_billing === 1)
                    const addressToUse = defaultAddress || data.addresses[0]
                    
                    if (addressToUse) {
                        console.log('TopBar - Address found:', addressToUse)
                        
                        // Extract country and pincode
                        const country = addressToUse.country_name || ''
                        const pincode = addressToUse.zipcode || ''
                        
                        // Format: "Country, Pincode" or just "Country" if no pincode
                        let locationString = country
                        if (pincode) {
                            locationString += (locationString ? ', ' : '') + pincode
                        }
                        
                        console.log('TopBar - Formatted location:', locationString)
                        return locationString
                    }
                }
            }
            return null
        } catch (error) {
            console.log('TopBar - Error fetching addresses:', error)
            return null
        }
    }

    // Function to get location from coordinates
    const getLocationFromCoordinates = async (latitude, longitude) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            )
            const data = await response.json()
            if (data && data.address) {
                // Extract country and postcode for consistency
                const country = data.address.country || ""
                const postcode = data.address.postcode || ""
                
                // Format: "Country, Postcode" or just "Country" if no postcode
                let locationString = country
                if (postcode) {
                    locationString += (locationString ? ", " : "") + postcode
                }
                
                return locationString || "Location found"
            } else {
                return "Location found"
            }
        } catch (e) {
            console.error('Error getting location from coordinates:', e)
            return "Location found"
        }
    }

    // Function to refresh location
    const refreshLocation = async () => {
        setIsLoading(true)
        setLocationError(false)
        setCurrentLocation("Refreshing location...")
        
        await initializeLocation()
    }

    const initializeLocation = async () => {
        try {
            // First, check if user is authenticated and has saved addresses
            const accessToken = getAccessTokenFromCache()
            if (accessToken) {
                console.log('TopBar - User is authenticated, checking saved addresses...')
                const savedAddress = await getUserAddresses()
                
                if (savedAddress) {
                    console.log('TopBar - Using saved address:', savedAddress)
                    setCurrentLocation(savedAddress)
                    setIsLoading(false)
                    return // Don't fetch geolocation if we have a saved address
                } else {
                    console.log('TopBar - No saved addresses found, using geolocation')
                }
            }

            // Fallback to geolocation if not authenticated or no saved addresses
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords
                        const locationString = await getLocationFromCoordinates(latitude, longitude)
                        setCurrentLocation(locationString)
                        setIsLoading(false)
                    },
                    (error) => {
                        console.error('Geolocation error:', error)
                        setCurrentLocation("Location unavailable")
                        setLocationError(true)
                        setIsLoading(false)
                    },
                    {
                        timeout: 10000, // 10 second timeout
                        enableHighAccuracy: false,
                        maximumAge: 300000 // 5 minutes cache
                    }
                )
            } else {
                setCurrentLocation("Location not supported")
                setLocationError(true)
                setIsLoading(false)
            }
        } catch (error) {
            console.error('Error initializing location:', error)
            setCurrentLocation("Location error")
            setLocationError(true)
            setIsLoading(false)
        }
    }

    // Language selection handler
    const handleLanguageChange = (language) => {
        setSelectedLanguage(language)
        setShowLanguageDropdown(false)
        
        // Here you can add logic to change the application language
        // For example, setting a cookie, updating localStorage, or calling an API
        localStorage.setItem('selectedLanguage', JSON.stringify(language))
        
        // Optional: Reload the page to apply language changes
        // window.location.reload()
    }

    // Click outside handler for language dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
                setShowLanguageDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    // Initialize default language
    useEffect(() => {
        const savedLanguage = localStorage.getItem('selectedLanguage')
        if (savedLanguage) {
            setSelectedLanguage(JSON.parse(savedLanguage))
        } else {
            // Set default language (first in the array)
            setSelectedLanguage(languages[0])
        }
    }, [])

    useEffect(() => {
        initializeLocation()
    }, [])

    return <div className="main">
        <div className=' container'>
            <div className='Header_section'>
                <div className='left_div'>
                    <a href={BRIGIZ_APP_E_COM_COMPARE_PRODUCT}>
                        <CompareIcon />
                    </a>
                    {/* Location Display */}
                    <div 
                        className="location-display"
                        title="Click to add address"
                        onClick={() => {
                            const accessToken = getAccessTokenFromCache()
                            if (accessToken) {
                                navigate('/edit-profile#address')
                            } else {
                                navigate('/login')
                            }
                        }}
                    >
                        <span className="location-icon">📍</span>
                        <span className="location-text">{currentLocation}</span>
                        {locationError && (
                            <span className="refresh-hint">(Click to refresh)</span>
                        )}
                    </div>
                </div>
                <div className='right_div'>
                    <a href={BRIGIZ_APP_E_COM_WISHLIST}>
                        <ThinLoveIcon />
                    </a>
                    <a href={BRIGIZ_APP_E_COM_CART}>
                        <ThinBagIcon />
                    </a>
                    <button onClick={()=>navigate('/contactUs')}>Contact Us</button>
                    <button>INR</button>
                    
                    {/* Language Selector */}
                    <div className="language-selector" ref={languageDropdownRef}>
                        <button 
                            className="language-button"
                            onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                        >
                            {selectedLanguage ? (
                                <>
                                    <span className="language-flag">{selectedLanguage.flag}</span>
                                    <span className="language-name">{selectedLanguage.name}</span>
                                </>
                            ) : (
                                'Select Language'
                            )}
                        </button>
                        
                        {showLanguageDropdown && (
                            <div className="language-dropdown">
                                {languages.map((language) => (
                                    <button
                                        key={language.code}
                                        className={`language-option ${selectedLanguage?.code === language.code ? 'active' : ''}`}
                                        onClick={() => handleLanguageChange(language)}
                                    >
                                        <span className="language-flag">{language.flag}</span>
                                        <span className="language-name">{language.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </div>
}

export default TopBar